import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Calendar as CalendarIcon, Plus, X, List, Trash2, Ban, Bell, Check, Edit3, Video, Copy, ExternalLink, Cloud, AlertTriangle, Clock, Users, MapPin, Search } from 'lucide-react';
import { calendarApi, clientsApi, bookingSlotsApi, bookingsApi, remindersApi, integrationsApi, sessionsApi } from '../services/api';
import { useTour } from '../hooks/useTour';
import { calendarTourSteps } from '../tours/calendarTour';
import { useToast } from '../context/ToastContext';
import { SkeletonCalendar } from '../components/Skeleton';
import './Pages.css';

interface OnlineSessionInfo {
  platform: string;
  meeting_link: string | null;
  access_code: string | null;
  meeting_id: string | null;
}

interface CalendarEvent {
  id: string;
  type: string;
  title: string;
  start: string;
  end: string;
  status: string;
  color: string;
  client_id?: string | null;
  case_id?: string | null;
  location_type?: string;
  online_session?: OnlineSessionInfo | null;
  google_event_id?: string | null;
}

interface Slot {
  id: string;
  start_time: string;
  end_time: string;
  slot_type: string;
  is_available: boolean;
  location_type: string;
  capacity: number;
}

interface Booking {
  id: string;
  booking_reference: string;
  booking_slot_id: string | null;
  client_id: string | null;
  status: string;
  notes: string | null;
  cancellation_reason: string | null;
  confirmed_at: string | null;
  cancelled_at: string | null;
  guest_name: string | null;
  guest_email: string | null;
  guest_message: string | null;
  reminder_sent: boolean;
  created_at: string;
}

interface Client {
  id: string;
  first_name: string;
  last_name: string;
}

type PageTab = 'calendar' | 'slots' | 'bookings';
type ViewMode = 'month' | 'week' | 'list';

/** Lokales Datum als YYYY-MM-DD (OHNE UTC-Konvertierung). */
const toLocalDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const statusLabels: Record<string, string> = {
  confirmed: 'Bestätigt',
  pending: 'Ausstehend',
  cancelled: 'Storniert',
  completed: 'Abgeschlossen',
  no_show: 'Nicht erschienen',
};

const locationLabels: Record<string, string> = {
  in_person: 'Vor Ort',
  online: 'Online',
  both: 'Beides',
};

export default function CalendarPage() {
  const { showToast } = useToast();
  // Page-level tabs
  const [pageTab, setPageTab] = useState<PageTab>('calendar');
  useTour('calendar', calendarTourSteps, { delay: 800 });

  // Calendar state
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedDay, setSelectedDay] = useState<string | null>(toLocalDateStr(new Date()));
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  // Booking state
  const [slots, setSlots] = useState<Slot[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [showSlotForm, setShowSlotForm] = useState(false);
  const [editingSlot, setEditingSlot] = useState<Slot | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [highlightedDay, setHighlightedDay] = useState<string | null>(null);
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingFilter, setBookingFilter] = useState('all');
  const [bookingPage, setBookingPage] = useState(0);
  const [clients, setClients] = useState<Client[]>([]);

  useEffect(() => {
    if (pageTab === 'calendar') loadEvents();
  }, [currentDate, viewMode, pageTab]); // eslint-disable-line

  useEffect(() => {
    if (pageTab === 'slots' || pageTab === 'bookings') {
      loadSlots();
      loadBookings();
      if (clients.length === 0) {
        clientsApi.list({ limit: 500 }).then(res => setClients(res.data.items)).catch(() => {});
      }
    }
  }, [pageTab]); // eslint-disable-line

  // Helper: get client name from client_id
  const getClientName = (clientId: string | null) => {
    if (!clientId) return null;
    const c = clients.find(cl => cl.id === clientId);
    return c ? `${c.first_name} ${c.last_name}` : null;
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  // --- Calendar functions ---
  const loadEvents = async () => {
    setLoadingEvents(true);
    const { fromDate, toDate } = getDateRange();
    try {
      const res = await calendarApi.events({ from_date: fromDate, to_date: toDate });
      setEvents(res.data.events);
    } catch { /* ignore */ }
    finally { setLoadingEvents(false); }
  };

  const getDateRange = () => {
    if (viewMode === 'month' || viewMode === 'list') {
      const y = currentDate.getFullYear();
      const m = currentDate.getMonth();
      const first = new Date(y, m, 1);
      const last = new Date(y, m + 1, 0);
      const startDay = (first.getDay() + 6) % 7;
      const from = new Date(first);
      from.setDate(from.getDate() - startDay);
      const to = new Date(last);
      const endDay = (last.getDay() + 6) % 7;
      to.setDate(to.getDate() + (6 - endDay));
      return { fromDate: toLocalDateStr(from), toDate: toLocalDateStr(to) };
    } else {
      const start = getWeekStart(currentDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { fromDate: toLocalDateStr(start), toDate: toLocalDateStr(end) };
    }
  };

  const getWeekStart = (d: Date) => {
    const result = new Date(d);
    const day = (result.getDay() + 6) % 7;
    result.setDate(result.getDate() - day);
    return result;
  };

  const navigate = (dir: number) => {
    const d = new Date(currentDate);
    if (viewMode === 'month' || viewMode === 'list') d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir * 7);
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());

  const getEventsForDate = (dateStr: string) => events.filter((e) => toLocalDateStr(new Date(e.start)) === dateStr);

  const handleExportIcs = async () => {
    try {
      const res = await calendarApi.exportIcs();
      const blob = new Blob([res.data], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CoachDesk365_Kalender.ics';
      a.click();
      URL.revokeObjectURL(url);
    } catch { showToast('Export fehlgeschlagen', 'error'); }
  };

  const fmtTime = (isoStr: string) =>
    new Date(isoStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  const fmtDate = (isoStr: string) =>
    new Date(isoStr).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const fmtDateShort = (isoStr: string) =>
    new Date(isoStr).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });

  const fmtDateTime = (iso: string) =>
    new Date(iso).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  const fmtDateTimeLong = (iso: string) =>
    new Date(iso).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // --- Booking functions ---
  const loadSlots = async () => {
    const res = await bookingSlotsApi.list();
    setSlots(res.data.items);
  };

  const loadBookings = async () => {
    const res = await bookingsApi.list();
    setBookings(res.data.items);
  };

  const deleteSlot = async (id: string) => {
    await bookingSlotsApi.delete(id);
    loadSlots();
  };

  const cancelBooking = async (id: string) => {
    await bookingsApi.update(id, { status: 'cancelled', cancellation_reason: 'Vom Coach storniert' });
    loadBookings();
    loadSlots();
  };

  const confirmBooking = async (id: string) => {
    await bookingsApi.update(id, { status: 'confirmed' });
    loadBookings();
  };

  // Calendar helpers
  const generateMonthGrid = () => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const first = new Date(y, m, 1);
    const startDay = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const days: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = startDay - 1; i >= 0; i--) days.push({ date: new Date(y, m, -i), isCurrentMonth: false });
    for (let i = 1; i <= daysInMonth; i++) days.push({ date: new Date(y, m, i), isCurrentMonth: true });
    while (days.length % 7 !== 0) {
      const lastDate = days[days.length - 1].date;
      const next = new Date(lastDate);
      next.setDate(next.getDate() + 1);
      days.push({ date: next, isCurrentMonth: false });
    }
    return days;
  };

  const generateWeekDays = () => {
    const start = getWeekStart(currentDate);
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  };

  const today = toLocalDateStr(new Date());
  const typeLabels: Record<string, string> = { session: 'Sitzung', booking: 'Buchung', slot: 'Slot' };
  const selectedDayEvents = selectedDay ? getEventsForDate(selectedDay).filter((e) => e.type !== 'slot') : [];
  const locationIcons: Record<string, string> = { in_person: 'Vor Ort', online: 'Online', both: 'Vor Ort/Online' };

  // Verfügbare Slots für Sidebar (nächste 14 Tage, gruppiert nach Datum)
  const nowStr = new Date().toISOString();
  const upcomingSlots = events
    .filter((e) => e.type === 'slot' && e.status === 'available' && e.start >= nowStr)
    .sort((a, b) => a.start.localeCompare(b.start))
    .slice(0, 20);
  const slotsByDate: Record<string, CalendarEvent[]> = {};
  upcomingSlots.forEach((slot) => {
    const dateStr = toLocalDateStr(new Date(slot.start));
    if (!slotsByDate[dateStr]) slotsByDate[dateStr] = [];
    slotsByDate[dateStr].push(slot);
  });
  const sortedEvents = [...events].filter((e) => e.type !== 'slot').sort((a, b) => a.start.localeCompare(b.start));

  return (
    <div>
      {/* Page Header */}
      <div className="flex-between mb-24">
        <div>
          <h1>Kalender & Buchungen</h1>
          <p className="text-muted text-sm">Termine, Verfügbarkeiten und Buchungen verwalten</p>
        </div>
        <div className="flex gap-8">
          {pageTab === 'calendar' && (
            <>
              <button className="btn btn-primary" data-tour="cal-new-event" onClick={() => setShowNewAppointment(true)}>
                <Plus size={16} /> Neuer Termin
              </button>
              <button className="btn btn-secondary" onClick={handleExportIcs}>
                <Download size={16} /> ICS exportieren
              </button>
            </>
          )}
          {pageTab === 'slots' && (
            <button className="btn btn-secondary" onClick={() => setShowSlotForm(true)}>
              <Plus size={18} /> Slot hinzufügen
            </button>
          )}
          {pageTab === 'bookings' && (
            <>
              <button
                className="btn btn-secondary"
                onClick={async () => {
                  setSendingReminders(true);
                  try {
                    const res = await remindersApi.checkAppointments();
                    const { checked, sent } = res.data;
                    showToast(sent > 0
                      ? `${sent} Erinnerung(en) versendet (${checked} Termine geprüft).`
                      : `Keine Erinnerungen zu versenden (${checked} Termine geprüft).`,
                      sent > 0 ? 'success' : 'info');
                  } catch (err: any) {
                    showToast(err.response?.data?.detail || 'Fehler beim Versenden der Erinnerungen', 'error');
                  } finally { setSendingReminders(false); }
                }}
                disabled={sendingReminders}
                data-tour="cal-reminders"
              >
                <Bell size={16} /> {sendingReminders ? 'Sende...' : 'Erinnerungen'}
              </button>
              <button className="btn btn-primary" onClick={() => setShowBookingForm(true)}>
                <Plus size={18} /> Neue Buchung
              </button>
            </>
          )}
        </div>
      </div>

      {/* Page Tabs */}
      <div className="pill-tabs mb-24" data-tour="cal-tabs">
        <button className={`pill-tab ${pageTab === 'calendar' ? 'pill-tab-active' : ''}`} onClick={() => setPageTab('calendar')}>
          <CalendarIcon size={15} /> Kalender
        </button>
        <button className={`pill-tab ${pageTab === 'slots' ? 'pill-tab-active' : ''}`} onClick={() => setPageTab('slots')}>
          <Clock size={15} /> Verfügbarkeiten
          {slots.filter(s => s.is_available).length > 0 && <span className="pill-tab-badge">{slots.filter(s => s.is_available).length}</span>}
        </button>
        <button className={`pill-tab ${pageTab === 'bookings' ? 'pill-tab-active' : ''}`} onClick={() => setPageTab('bookings')}>
          <Users size={15} /> Buchungen
          {bookings.length > 0 && <span className="pill-tab-badge">{bookings.length}</span>}
        </button>
      </div>

      {/* ==================== CALENDAR TAB ==================== */}
      {pageTab === 'calendar' && (
        <>
          {/* Controls */}
          <div className="cal-controls">
            <div className="cal-nav">
              <button className="cal-nav-btn" onClick={() => navigate(-1)}><ChevronLeft size={16} /></button>
              <button className="cal-today-btn" onClick={goToday}>Heute</button>
              <button className="cal-nav-btn" onClick={() => navigate(1)}><ChevronRight size={16} /></button>
              <h2 className="cal-month-title">
                {viewMode === 'week'
                  ? `KW ${getWeekNumber(currentDate)} — ${currentDate.getFullYear()}`
                  : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
              </h2>
            </div>
            <div className="chart-toggle" data-tour="cal-view-modes">
              <button className={`chart-toggle-btn ${viewMode === 'month' ? 'chart-toggle-btn-active' : ''}`} onClick={() => setViewMode('month')}>Monat</button>
              <button className={`chart-toggle-btn ${viewMode === 'week' ? 'chart-toggle-btn-active' : ''}`} onClick={() => setViewMode('week')}>Woche</button>
              <button className={`chart-toggle-btn ${viewMode === 'list' ? 'chart-toggle-btn-active' : ''}`} onClick={() => setViewMode('list')}>
                <List size={12} style={{ marginRight: 2, verticalAlign: 'middle' }} /> Liste
              </button>
            </div>
          </div>

          {/* List View */}
          {viewMode === 'list' && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {sortedEvents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                  <CalendarIcon size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                  <p className="text-sm">Keine Termine in diesem Zeitraum.</p>
                </div>
              ) : (
                <table className="table">
                  <thead><tr><th>Datum</th><th>Zeit</th><th>Titel</th><th>Ort</th><th>Typ</th><th>Status</th></tr></thead>
                  <tbody>
                    {sortedEvents.map((ev) => (
                      <tr key={ev.id} onClick={() => setSelectedEvent(ev)} style={{ cursor: 'pointer' }}>
                        <td style={{ fontWeight: 500 }}>{fmtDateShort(ev.start)}</td>
                        <td className="text-light">{fmtTime(ev.start)} – {fmtTime(ev.end)}</td>
                        <td>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {ev.title}
                            {ev.online_session?.meeting_link && <Video size={14} style={{ color: '#2D8CFF', flexShrink: 0 }} />}
                          </span>
                        </td>
                        <td className="text-light">
                          {ev.location_type === 'online' ? 'Online' : ev.location_type === 'in_person' ? 'Vor Ort' : ev.location_type === 'both' ? 'Beides' : '—'}
                        </td>
                        <td>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--primary)', marginRight: 6, verticalAlign: 'middle' }} />
                          {typeLabels[ev.type] || ev.type}
                        </td>
                        <td>
                          <span className={`badge ${ev.status === 'confirmed' ? 'badge-active' : ev.status === 'cancelled' ? 'badge-paused' : 'badge-new'}`}>
                            {ev.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Month/Week View */}
          {viewMode !== 'list' && loadingEvents && (
            <div className="card" style={{ padding: 20 }}>
              <SkeletonCalendar />
            </div>
          )}
          {viewMode !== 'list' && !loadingEvents && (() => {
            // Mini-stats
            const allNonSlot = events.filter(e => e.type !== 'slot');
            const sessionCount = allNonSlot.filter(e => e.type === 'session').length;
            const bookingCount = allNonSlot.filter(e => e.type === 'booking').length;
            const freeSlotCount = events.filter(e => e.type === 'slot' && e.status === 'available').length;
            const totalSlots = freeSlotCount + bookingCount;
            const utilization = totalSlots > 0 ? Math.round((bookingCount / totalSlots) * 100) : 0;
            const wkDay = (d: Date) => (d.getDay() + 6) % 7;

            return (
            <div className="cal-2col">
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {viewMode === 'month' ? (
                  <div className="cal-grid">
                    <div className="cal-header-row">
                      {dayNames.map((d, i) => <div key={d} className="cal-header-cell" style={i >= 5 ? { opacity: 0.5 } : undefined}>{d}</div>)}
                    </div>
                    {chunkArray(generateMonthGrid(), 7).map((week, wi) => (
                      <div className="cal-week-row" key={wi}>
                        {week.map(({ date: d, isCurrentMonth }) => {
                          const dateStr = toLocalDateStr(d);
                          const dayEvents = getEventsForDate(dateStr).filter((e) => e.type !== 'slot');
                          const isToday = dateStr === today;
                          const isSelected = dateStr === selectedDay;
                          return (
                            <div key={dateStr} className={`cal-day-cell ${!isCurrentMonth ? 'cal-other-month' : ''} ${isToday ? 'cal-today' : ''} ${isSelected ? 'cal-selected' : ''} ${dateStr === highlightedDay ? 'cal-day-highlight' : ''}`} onClick={() => setSelectedDay(dateStr)}>
                              <span className="cal-day-num">{d.getDate()}</span>
                              <div className="cal-events">
                                {dayEvents.slice(0, 3).map((ev) => (
                                  <div
                                    key={ev.id}
                                    className={`cal-event-chip ${ev.type === 'booking' ? 'cal-event-chip-booking' : ''}`}
                                    title={`${fmtTime(ev.start)} — ${ev.title}${ev.online_session?.meeting_link ? ' (Zoom)' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); setSelectedDay(dateStr); }}
                                  >
                                    <span className="cal-event-chip-time">{fmtTime(ev.start)}</span>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.title}</span>
                                  </div>
                                ))}
                                {dayEvents.length > 3 && (
                                  <span className="cal-more" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setSelectedDay(dateStr); }}>+{dayEvents.length - 3} weitere</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="cal-week-view">
                    {generateWeekDays().map((d) => {
                      const dateStr = toLocalDateStr(d);
                      const dayEvents = getEventsForDate(dateStr).filter((e) => e.type !== 'slot');
                      const isToday = dateStr === today;
                      return (
                        <div key={dateStr} className={`cal-week-day ${isToday ? 'cal-today' : ''} ${dateStr === highlightedDay ? 'cal-day-highlight' : ''}`} onClick={() => setSelectedDay(dateStr)}>
                          <div className="cal-week-day-header">
                            <span className="cal-week-day-name" style={wkDay(d) >= 5 ? { opacity: 0.5 } : undefined}>{dayNames[wkDay(d)]}</span>
                            <span className="cal-week-day-num">{d.getDate()}.{d.getMonth() + 1}.</span>
                          </div>
                          <div className="cal-week-day-events">
                            {dayEvents.map((ev) => (
                              <div
                                key={ev.id}
                                className="cal-week-event"
                                style={{ borderLeftColor: ev.type === 'booking' ? 'var(--accent, #d97706)' : 'var(--primary)', cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); setSelectedDay(dateStr); }}
                              >
                                <span className="cal-week-event-time">{fmtTime(ev.start)} - {fmtTime(ev.end)}</span>
                                <span className="cal-week-event-title" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  {ev.title}
                                  {ev.online_session?.meeting_link && <Video size={12} style={{ color: '#2D8CFF', flexShrink: 0 }} />}
                                </span>
                              </div>
                            ))}
                            {dayEvents.length === 0 && <span className="text-muted text-sm">Keine Termine</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Right Panel */}
              <div className="cal-right-panel">
                {/* Mini Stats — Row-based like mockup */}
                <div className="mini-stats-card">
                  <div className="mini-stats-title">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()} auf einen Blick</div>
                  <div className="stat-row">
                    <span className="stat-row-label">Sitzungen</span>
                    <span className="stat-row-val" style={{ color: 'var(--primary)' }}>{sessionCount}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-row-label">Buchungen</span>
                    <span className="stat-row-val" style={{ color: 'var(--info, var(--accent, #d97706))' }}>{bookingCount}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-row-label">Freie Slots</span>
                    <span className="stat-row-val" style={{ color: 'var(--success, #22c55e)' }}>{freeSlotCount}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-row-label">Auslastung</span>
                    <span className="stat-row-val" style={{ color: 'var(--accent, var(--primary))' }}>{utilization} %</span>
                  </div>
                </div>

                {/* Day Detail Panel */}
                <div className={`day-detail-card ${selectedDay === highlightedDay ? 'day-panel-highlight' : ''}`}>
                  {selectedDay ? (
                    <>
                      <div className="day-detail-header">
                        <h3>{fmtDate(selectedDay + 'T00:00:00')}</h3>
                        {selectedDay === today && <span className="today-badge">Heute</span>}
                      </div>
                      <div style={{ padding: '8px 0' }}>
                        {selectedDayEvents.length === 0 ? (
                          <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                            <CalendarIcon size={28} style={{ marginBottom: 8, opacity: 0.4, display: 'block', margin: '0 auto 8px' }} />
                            Keine Termine heute
                          </div>
                        ) : (
                          selectedDayEvents.map((ev) => (
                            <div
                              key={ev.id}
                              className={`day-event-item ${ev.type === 'booking' ? 'booking-item' : 'session-item'}`}
                              onClick={() => setSelectedEvent(ev)}
                            >
                              <div className="evt-time-col">
                                {fmtTime(ev.start)}<br />{fmtTime(ev.end)}
                              </div>
                              <div className="evt-detail">
                                <h4 style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  {ev.title}
                                  {ev.online_session?.meeting_link && <Video size={12} style={{ color: '#2D8CFF' }} />}
                                </h4>
                                <p>{locationIcons[ev.location_type || ''] || ''}</p>
                                <span className={`evt-badge ${ev.type === 'booking' ? 'booking' : 'session'}`}>
                                  {typeLabels[ev.type] || ev.type}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Verfügbare Zeiten in day detail */}
                      {(() => {
                        const daySlots = selectedDay ? events.filter(e => e.type === 'slot' && e.status === 'available' && toLocalDateStr(new Date(e.start)) === selectedDay) : [];
                        if (daySlots.length === 0) return null;
                        return (
                          <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                              <h4 style={{ margin: 0, fontSize: '0.82rem', fontWeight: 600 }}>Verfügbare Zeiten</h4>
                              <span style={{ background: 'var(--success-bg, rgba(34,197,94,0.1))', color: 'var(--success, #22c55e)', fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>{daySlots.length} frei</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                              {daySlots.map((slot) => (
                                <div key={slot.id} className="available-time-row">
                                  <span style={{ color: 'var(--text)' }}>{fmtTime(slot.start)} – {fmtTime(slot.end)}</span>
                                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{locationIcons[slot.location_type || 'both'] || ''}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-muted)' }}>
                      <CalendarIcon size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
                      <p className="text-sm">Tag anklicken für Details</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })()}

          {/* Legend */}
          <div className="flex gap-16 mt-16" style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--primary)', marginRight: 6 }} />Sitzung</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--primary-light, var(--primary))', opacity: 0.7, marginRight: 6 }} />Buchung</span>
          </div>
        </>
      )}

      {/* ==================== SLOTS TAB ==================== */}
      {pageTab === 'slots' && (
        (() => {
          const freeSlotsList = slots.filter(s => s.is_available);
          const bookedCount = slots.filter(s => !s.is_available).length;

          // Group by date
          const groupByDateFull = (list: Slot[]) => {
            const groups: Record<string, Slot[]> = {};
            const sortedList = [...list].sort((a, b) => a.start_time.localeCompare(b.start_time));
            sortedList.forEach(s => {
              const dateStr = toLocalDateStr(new Date(s.start_time));
              if (!groups[dateStr]) groups[dateStr] = [];
              groups[dateStr].push(s);
            });
            return Object.entries(groups);
          };

          const fmtFullDate = (dateStr: string) =>
            new Date(dateStr + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

          const calcDuration = (s: Slot) => {
            const start = new Date(s.start_time);
            const end = new Date(s.end_time);
            return Math.round((end.getTime() - start.getTime()) / 60000);
          };

          return (
            <>
              {/* Summary strip */}
              <div className="avail-summary-strip">
                <div className="avail-summary-item">
                  <span className="avail-summary-dot free" />
                  <span className="avail-summary-val">{freeSlotsList.length}</span>
                  <span className="avail-summary-label">Freie Slots</span>
                </div>
                <div className="avail-summary-item">
                  <span className="avail-summary-dot booked" />
                  <span className="avail-summary-val">{bookedCount}</span>
                  <span className="avail-summary-label">Gebucht</span>
                  <span className="avail-summary-hint">(siehe Buchungen-Tab)</span>
                </div>
                <div className="avail-summary-item">
                  <span className="avail-summary-val" style={{ color: 'var(--text-muted)' }}>{slots.length}</span>
                  <span className="avail-summary-label">Gesamt</span>
                </div>
              </div>

              {freeSlotsList.length === 0 ? (
                <div className="empty-state card" style={{ textAlign: 'center', padding: '40px 24px' }}>
                  <Clock size={32} style={{ color: 'var(--text-muted)', opacity: 0.4, marginBottom: 12 }} />
                  <p style={{ fontSize: '0.92rem', fontWeight: 500, marginBottom: 4 }}>Keine freien Slots vorhanden</p>
                  <p className="text-muted text-sm" style={{ marginBottom: 16 }}>Erstelle Verfügbarkeiten, damit Klienten Termine buchen können.</p>
                  <button className="btn btn-primary" onClick={() => setShowSlotForm(true)}>
                    <Plus size={16} /> Verfügbarkeit eintragen
                  </button>
                </div>
              ) : (
                <div className="slots-card">
                  <div className="slots-card-header">
                    <h3>Verfügbare Zeiten</h3>
                    <span className="slots-count-badge free">{freeSlotsList.length} frei</span>
                  </div>
                  {groupByDateFull(freeSlotsList).map(([dateStr, dateSlots]) => (
                    <div className="slots-date-group" key={dateStr}>
                      <div className="slots-date-label">
                        <span>{fmtFullDate(dateStr)}</span>
                        <span className="slot-count">{dateSlots.length} Slot{dateSlots.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="slot-pills-wrap">
                        {dateSlots.map(s => (
                          <div key={s.id} className="slot-pill-free slot-pill-clickable" title={`${locationLabels[s.location_type] || s.location_type} · ${calcDuration(s)} Min. — Klicken zum Bearbeiten`} onClick={() => setEditingSlot(s)}>
                            <Clock size={12} />
                            <span>{fmtTime(s.start_time)} – {fmtTime(s.end_time)}</span>
                            <span className="slot-pill-loc">{s.location_type === 'online' ? 'Online' : s.location_type === 'in_person' ? 'Vor Ort' : 'Hybrid'}</span>
                            <button className="slot-pill-edit" onClick={(e) => { e.stopPropagation(); setEditingSlot(s); }} title="Bearbeiten"><Edit3 size={10} /></button>
                            <button className="slot-pill-del" onClick={(e) => { e.stopPropagation(); deleteSlot(s.id); }} title="Löschen"><X size={10} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()
      )}

      {/* ==================== BOOKINGS TAB ==================== */}
      {pageTab === 'bookings' && (() => {
        const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;
        const cancelledCount = bookings.filter(b => b.status === 'cancelled').length;
        const guestCount = bookings.filter(b => b.guest_name && !b.client_id).length;
        const confirmRate = bookings.length > 0 ? Math.round((confirmedCount / bookings.length) * 100) : 0;
        const cancelRate = bookings.length > 0 ? Math.round((cancelledCount / bookings.length) * 100) : 0;

        // Find slot for a booking
        const getSlotForBooking = (b: Booking) => b.booking_slot_id ? slots.find(s => s.id === b.booking_slot_id) : null;

        // Resolve display name: client_id → clients lookup, else guest_name
        const getDisplayName = (b: Booking) => {
          if (b.client_id) return getClientName(b.client_id) || b.guest_name || null;
          return b.guest_name || null;
        };

        // Filter + Search
        let filtered = bookings;
        if (bookingFilter !== 'all') {
          filtered = filtered.filter(b => b.status === bookingFilter);
        }
        if (bookingSearch.trim()) {
          const q = bookingSearch.toLowerCase();
          filtered = filtered.filter(b =>
            (b.booking_reference || '').toLowerCase().includes(q) ||
            (getDisplayName(b) || '').toLowerCase().includes(q) ||
            (b.guest_email || '').toLowerCase().includes(q) ||
            (b.notes || '').toLowerCase().includes(q)
          );
        }

        // Pagination
        const page = bookingPage;
        const perPage = 10;
        const totalPages = Math.ceil(filtered.length / perPage);
        const pagedBookings = filtered.slice(page * perPage, (page + 1) * perPage);

        return (
          <>
            {/* KPI Strip with sub-text */}
            {bookings.length > 0 && (
              <div className="bookings-kpi-strip">
                <div className="bookings-kpi">
                  <div className="bookings-kpi-label">Buchungen gesamt</div>
                  <div className="bookings-kpi-value">{bookings.length}</div>
                  <div className="bookings-kpi-sub">{monthNames[new Date().getMonth()]} {new Date().getFullYear()}</div>
                </div>
                <div className="bookings-kpi">
                  <div className="bookings-kpi-label">Bestätigt</div>
                  <div className="bookings-kpi-value" style={{ color: 'var(--success, #22c55e)' }}>{confirmedCount}</div>
                  <div className="bookings-kpi-sub">{confirmRate} % Bestätigungsrate</div>
                </div>
                <div className="bookings-kpi">
                  <div className="bookings-kpi-label">Storniert</div>
                  <div className="bookings-kpi-value" style={{ color: 'var(--danger, #ef4444)' }}>{cancelledCount}</div>
                  <div className="bookings-kpi-sub">{cancelRate} % Stornierungsrate</div>
                </div>
                <div className="bookings-kpi">
                  <div className="bookings-kpi-label">Ohne Klient</div>
                  <div className="bookings-kpi-value" style={{ color: 'var(--warning, #f59e0b)' }}>{guestCount}</div>
                  <div className="bookings-kpi-sub">Gast-Buchungen</div>
                </div>
              </div>
            )}

            {bookings.length === 0 ? (
              <div className="empty-state card">
                <p>Noch keine Buchungen vorhanden.</p>
              </div>
            ) : (
              <div className="bookings-table-card">
                {/* Table header with search + filter chips */}
                <div className="bookings-table-header">
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1 }}>
                    <div className="bookings-search-wrap">
                      <Search size={14} />
                      <input
                        className="bookings-search-input"
                        placeholder="Suche (Referenz, Klient…)"
                        value={bookingSearch}
                        onChange={(e) => { setBookingSearch(e.target.value); setBookingPage(0); }}
                      />
                    </div>
                    <div className="filter-chips">
                      {['all', 'confirmed', 'cancelled', 'pending'].map((f) => (
                        <button key={f} className={`filter-chip ${bookingFilter === f ? 'active' : ''}`} onClick={() => { setBookingFilter(f); setBookingPage(0); }}>
                          {f === 'all' ? 'Alle' : f === 'confirmed' ? 'Bestätigt' : f === 'cancelled' ? 'Storniert' : 'Ausstehend'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <table className="table">
                  <thead>
                    <tr>
                      <th>Referenz</th>
                      <th>Klient / Gast</th>
                      <th>Termin-Slot</th>
                      <th>Erstellt</th>
                      <th>Notizen</th>
                      <th>Status</th>
                      <th>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedBookings.map((b) => {
                      const slot = getSlotForBooking(b);
                      return (
                        <tr key={b.id} onClick={() => setSelectedBooking(b)} style={{ cursor: 'pointer' }}>
                          <td><span className="booking-ref">{b.booking_reference}</span></td>
                          <td>
                            {(() => {
                              const displayName = getDisplayName(b);
                              const isGuest = !b.client_id;
                              return (
                                <div className="booking-client">
                                  {isGuest ? (
                                    <div className="guest-avatar">G</div>
                                  ) : (
                                    <div className="client-avatar-sm">{displayName ? getInitials(displayName) : '?'}</div>
                                  )}
                                  {displayName ? (
                                    <div>
                                      <div style={{ fontWeight: 500 }}>{displayName}</div>
                                      {b.guest_email && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{b.guest_email}</div>}
                                    </div>
                                  ) : isGuest ? (
                                    <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Gast</span>
                                  ) : (
                                    <span style={{ color: 'var(--text-light)' }}>—</span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td style={{ fontSize: '0.78rem' }}>
                            {slot ? (
                              `${new Date(slot.start_time).toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric', year: 'numeric' })} · ${fmtTime(slot.start_time)}–${fmtTime(slot.end_time)}`
                            ) : '—'}
                          </td>
                          <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{fmtDateTime(b.created_at)}</td>
                          <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.guest_message || b.notes || '—'}</td>
                          <td>
                            <span className={`status-dot ${b.status === 'confirmed' ? 'status-confirmed' : b.status === 'cancelled' ? 'status-cancelled' : 'status-pending'}`}>
                              {statusLabels[b.status] || b.status}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                              <button className="action-btn edit" onClick={() => setSelectedBooking(b)} title="Bearbeiten">
                                <Edit3 size={13} />
                              </button>
                              {(b.status === 'confirmed' || b.status === 'pending') && (
                                <button className="action-btn cancel" onClick={() => cancelBooking(b.id)} title="Stornieren">
                                  <X size={13} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="bookings-pagination">
                  <span>Zeige {filtered.length > 0 ? page * perPage + 1 : 0}–{Math.min((page + 1) * perPage, filtered.length)} von {filtered.length} Buchungen</span>
                  {totalPages > 1 && (
                    <div className="pagination-btns">
                      <button className="pagination-btn" onClick={() => setBookingPage(Math.max(0, page - 1))} disabled={page === 0}>
                        <ChevronLeft size={12} />
                      </button>
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                        <button key={i} className={`pagination-btn ${page === i ? 'active' : ''}`} onClick={() => setBookingPage(i)}>
                          {i + 1}
                        </button>
                      ))}
                      <button className="pagination-btn" onClick={() => setBookingPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>
                        <ChevronRight size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* ==================== MODALS ==================== */}
      {showNewAppointment && (
        <NewAppointmentModal
          onClose={() => setShowNewAppointment(false)}
          onSaved={(createdDate?: string) => {
            setShowNewAppointment(false);
            if (createdDate) {
              setCurrentDate(new Date(createdDate + 'T12:00:00'));
              setSelectedDay(createdDate);
              setHighlightedDay(createdDate);
              setTimeout(() => setHighlightedDay(null), 3000);
            }
            loadEvents();
          }}
        />
      )}
      {showSlotForm && (
        <SlotForm
          onClose={() => setShowSlotForm(false)}
          onSaved={(createdDate?: string) => {
            if (createdDate && pageTab === 'calendar') {
              setCurrentDate(new Date(createdDate + 'T12:00:00'));
              setSelectedDay(createdDate);
              setHighlightedDay(createdDate);
              setTimeout(() => setHighlightedDay(null), 3000);
              loadEvents();
            }
            loadSlots();
          }}
        />
      )}
      {editingSlot && (
        <SlotForm
          slot={editingSlot}
          onClose={() => setEditingSlot(null)}
          onSaved={() => { setEditingSlot(null); loadSlots(); if (pageTab === 'calendar') loadEvents(); }}
        />
      )}
      {showBookingForm && (
        <BookingForm
          slots={slots.filter((s) => s.is_available)}
          onClose={() => setShowBookingForm(false)}
          onSaved={() => { loadBookings(); loadSlots(); }}
        />
      )}
      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          slots={slots}
          onClose={() => setSelectedBooking(null)}
          onSaved={() => { loadBookings(); loadSlots(); setSelectedBooking(null); }}
        />
      )}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onZoomCreated={() => { setSelectedEvent(null); loadEvents(); }}
        />
      )}
    </div>
  );
}


// ==================== SUB-COMPONENTS ====================

function NewAppointmentModal({ onClose, onSaved }: { onClose: () => void; onSaved: (date?: string) => void }) {
  const { showToast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [clientId, setClientId] = useState('');
  const [locationType, setLocationType] = useState('both');
  const [videoPlatform, setVideoPlatform] = useState('zoom');
  const [notes, setNotes] = useState('');
  const [createBooking, setCreateBooking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zoomConnected, setZoomConnected] = useState(false);
  const [overlapConflicts, setOverlapConflicts] = useState<Array<{ id: string; start_time: string; end_time: string; title: string }>>([]);

  useEffect(() => {
    clientsApi.list({ limit: 200 }).then((res) => setClients(res.data.items)).finally(() => setLoadingClients(false));
    integrationsApi.zoomStatus().then((res) => setZoomConnected(res.data.connected)).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createBooking && loadingClients) return;
    setSaving(true);
    try {
      const slotRes = await bookingSlotsApi.create({
        start_time: `${date}T${startTime}:00`,
        end_time: `${date}T${endTime}:00`,
        location_type: locationType,
      });
      if (createBooking && slotRes.data.id) {
        await bookingsApi.create({
          booking_slot_id: slotRes.data.id,
          client_id: clientId || undefined,
          notes: notes || undefined,
        });
      }
      const selectedClient = clients.find((c) => c.id === clientId);
      const clientName = selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : null;
      if (createBooking) {
        showToast(clientName ? `Termin für ${clientName} erstellt` : 'Termin erstellt (ohne Klient)', 'success');
      } else {
        showToast('Verfügbarkeit eingetragen', 'success');
      }
      onSaved(date);
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.detail?.code === 'OVERLAP') {
        setOverlapConflicts(err.response.data.detail.conflicts);
      } else {
        const msg = typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Fehler beim Erstellen';
        showToast(msg, 'error');
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Neuer Termin</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group"><label>Datum *</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></div>
          <div className="form-row">
            <div className="form-group"><label>Von *</label><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required /></div>
            <div className="form-group"><label>Bis *</label><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required /></div>
          </div>
          <div className="form-group">
            <label>Ort</label>
            <select value={locationType} onChange={(e) => setLocationType(e.target.value)}>
              <option value="both">Vor Ort oder Online</option>
              <option value="in_person">Nur Vor Ort</option>
              <option value="online">Nur Online</option>
            </select>
          </div>
          {(locationType === 'online' || locationType === 'both') && (
            <div className="form-group">
              <label>Video-Plattform</label>
              <select value={videoPlatform} onChange={(e) => setVideoPlatform(e.target.value)}>
                <option value="none">Keine</option>
                <option value="zoom" disabled={!zoomConnected}>
                  Zoom {zoomConnected ? '' : '(nicht verbunden)'}
                </option>
                <option value="teams" disabled>Microsoft Teams (bald verfügbar)</option>
              </select>
              {videoPlatform === 'zoom' && zoomConnected && (
                <span style={{ fontSize: '0.78rem', color: '#5B8C5A', marginTop: 4, display: 'block' }}>
                  Zoom-Meeting wird automatisch erstellt, wenn eine Sitzung für diesen Termin angelegt wird.
                </span>
              )}
              {!zoomConnected && (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                  Zoom unter Einstellungen → Integrationen verbinden.
                </span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '10px 14px', background: 'var(--bg)', borderRadius: 8 }}>
            <input type="checkbox" id="createBooking" checked={createBooking} onChange={(e) => setCreateBooking(e.target.checked)} style={{ width: 18, height: 18 }} />
            <label htmlFor="createBooking" style={{ cursor: 'pointer', marginBottom: 0 }}>Direkt als Buchung anlegen</label>
          </div>
          {createBooking && (
            <>
              <div className="form-group">
                <label>Klient {loadingClients && <span className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 400 }}>(wird geladen...)</span>}</label>
                <select value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={loadingClients}>
                  <option value="">— Kein Klient —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Notizen</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div>
            </>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Erstellen...' : 'Termin erstellen'}</button>
          </div>
        </form>
        {overlapConflicts.length > 0 && (
          <OverlapWarningModal conflicts={overlapConflicts} onClose={() => setOverlapConflicts([])} />
        )}
      </div>
    </div>
  );
}

function SlotForm({ slot, onClose, onSaved }: { slot?: Slot | null; onClose: () => void; onSaved: (date?: string) => void }) {
  const { showToast } = useToast();
  const isEdit = !!slot;

  // Pre-fill from existing slot in edit mode
  const initDate = slot ? toLocalDateStr(new Date(slot.start_time)) : '';
  const initStart = slot ? new Date(slot.start_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false }) : '09:00';
  const initEnd = slot ? new Date(slot.end_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false }) : '10:00';

  const [date, setDate] = useState(initDate);
  const [startTime, setStartTime] = useState(initStart);
  const [endTime, setEndTime] = useState(initEnd);
  const [locationType, setLocationType] = useState(slot?.location_type || 'both');
  const [videoPlatform, setVideoPlatform] = useState('zoom');
  const [saving, setSaving] = useState(false);
  const [zoomConnected, setZoomConnected] = useState(false);
  const [overlapConflicts, setOverlapConflicts] = useState<Array<{ id: string; start_time: string; end_time: string; title: string }>>([]);

  useEffect(() => {
    integrationsApi.zoomStatus().then((res) => setZoomConnected(res.data.connected)).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { start_time: `${date}T${startTime}:00`, end_time: `${date}T${endTime}:00`, location_type: locationType };
      if (isEdit) {
        await bookingSlotsApi.update(slot!.id, payload);
        showToast('Slot aktualisiert', 'success');
      } else {
        await bookingSlotsApi.create(payload);
        showToast('Verfügbarkeit eingetragen', 'success');
      }
      onSaved(date);
      onClose();
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.detail?.code === 'OVERLAP') {
        setOverlapConflicts(err.response.data.detail.conflicts);
      } else {
        showToast(isEdit ? 'Fehler beim Aktualisieren' : 'Fehler beim Erstellen', 'error');
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2>{isEdit ? 'Slot bearbeiten' : 'Verfügbarkeit eintragen'}</h2><button className="btn-icon" onClick={onClose}><X size={20} /></button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group"><label>Datum *</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></div>
          <div className="form-row">
            <div className="form-group"><label>Von *</label><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required /></div>
            <div className="form-group"><label>Bis *</label><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required /></div>
          </div>
          <div className="form-group">
            <label>Ort</label>
            <select value={locationType} onChange={(e) => setLocationType(e.target.value)}>
              <option value="both">Vor Ort oder Online</option><option value="in_person">Nur Vor Ort</option><option value="online">Nur Online</option>
            </select>
          </div>
          {(locationType === 'online' || locationType === 'both') && (
            <div className="form-group">
              <label>Video-Plattform</label>
              <select value={videoPlatform} onChange={(e) => setVideoPlatform(e.target.value)}>
                <option value="none">Keine</option>
                <option value="zoom" disabled={!zoomConnected}>Zoom {zoomConnected ? '' : '(nicht verbunden)'}</option>
                <option value="teams" disabled>Microsoft Teams (bald verfügbar)</option>
              </select>
              {videoPlatform === 'zoom' && zoomConnected && (
                <span style={{ fontSize: '0.78rem', color: '#5B8C5A', marginTop: 4, display: 'block' }}>
                  Zoom-Meeting wird automatisch bei Sitzungserstellung angelegt.
                </span>
              )}
            </div>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Speichern...' : isEdit ? 'Änderungen speichern' : 'Slot anlegen'}</button>
          </div>
        </form>
        {overlapConflicts.length > 0 && (
          <OverlapWarningModal conflicts={overlapConflicts} onClose={() => setOverlapConflicts([])} />
        )}
      </div>
    </div>
  );
}

function BookingForm({ slots, onClose, onSaved }: { slots: Slot[]; onClose: () => void; onSaved: () => void }) {
  const { showToast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [slotId, setSlotId] = useState('');
  const [clientId, setClientId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { clientsApi.list({ limit: 200 }).then((res) => setClients(res.data.items)).finally(() => setLoadingClients(false)); }, []);

  const fmtSlot = (s: Slot) => {
    const d = new Date(s.start_time);
    const end = new Date(s.end_time);
    return `${d.toLocaleDateString('de-DE')} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await bookingsApi.create({ booking_slot_id: slotId || undefined, client_id: clientId || undefined, notes: notes || undefined });
      const selectedClient = clients.find((c) => c.id === clientId);
      const clientName = selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : null;
      showToast(clientName ? `Buchung für ${clientName} erstellt` : 'Buchung erstellt', 'success');
      onSaved();
      onClose();
    } catch { showToast('Fehler beim Erstellen der Buchung', 'error'); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2>Neue Buchung</h2><button className="btn-icon" onClick={onClose}><X size={20} /></button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Verfügbarer Slot</label>
            <select value={slotId} onChange={(e) => setSlotId(e.target.value)}>
              <option value="">— Slot wählen —</option>
              {slots.map((s) => <option key={s.id} value={s.id}>{fmtSlot(s)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Klient {loadingClients && <span className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 400 }}>(wird geladen...)</span>}</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={loadingClients}>
              <option value="">— Kein Klient —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Notizen</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={saving || loadingClients}>{saving ? 'Speichern...' : 'Buchung erstellen'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BookingDetailModal({ booking, slots, onClose, onSaved }: { booking: Booking; slots: Slot[]; onClose: () => void; onSaved: () => void }) {
  const { showToast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [status, setStatus] = useState(booking.status);
  const [notes, setNotes] = useState(booking.notes || '');
  const [guestName, setGuestName] = useState(booking.guest_name || '');
  const [guestEmail, setGuestEmail] = useState(booking.guest_email || '');
  const [guestMessage, setGuestMessage] = useState(booking.guest_message || '');
  const [clientId, setClientId] = useState(booking.client_id || '');
  const [slotId, setSlotId] = useState(booking.booking_slot_id || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { clientsApi.list({ limit: 200 }).then((res) => setClients(res.data.items)).finally(() => setLoadingClients(false)); }, []);

  const currentSlot = slots.find((s) => s.id === booking.booking_slot_id);
  const availableSlots = slots.filter((s) => s.is_available || s.id === booking.booking_slot_id);

  const fmtSlot = (s: Slot) => {
    const d = new Date(s.start_time);
    const end = new Date(s.end_time);
    return `${d.toLocaleDateString('de-DE')} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const fmtDateTimeLong = (iso: string) =>
    new Date(iso).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleSave = async () => {
    setSaving(true);
    try {
      await bookingsApi.update(booking.id, {
        status, notes: notes || null, guest_name: guestName || null, guest_email: guestEmail || null,
        guest_message: guestMessage || null, client_id: clientId || null, booking_slot_id: slotId || null,
      });
      onSaved();
      showToast('Buchung gespeichert', 'success');
    } catch (err: any) { showToast(err.response?.data?.detail || 'Fehler beim Speichern', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>Buchung {booking.booking_reference}</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>
        <div style={{ padding: '0 24px' }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <span className="text-muted" style={{ fontSize: '0.82rem' }}>Erstellt: {fmtDateTimeLong(booking.created_at)}</span>
            {booking.confirmed_at && <span className="text-muted" style={{ fontSize: '0.82rem' }}>Bestätigt: {fmtDateTimeLong(booking.confirmed_at)}</span>}
            {booking.cancelled_at && <span className="text-muted" style={{ fontSize: '0.82rem' }}>Storniert: {fmtDateTimeLong(booking.cancelled_at)}</span>}
            {booking.reminder_sent && <span className="badge badge-completed" style={{ fontSize: '0.75rem' }}>Erinnerung gesendet</span>}
          </div>
        </div>
        <div style={{ padding: '0 24px 16px' }}>
          <div className="form-group">
            <label>Termin-Slot</label>
            <select value={slotId} onChange={(e) => setSlotId(e.target.value)}>
              <option value="">— Kein Slot —</option>
              {availableSlots.map((s) => <option key={s.id} value={s.id}>{fmtSlot(s)}</option>)}
            </select>
            {currentSlot && <span className="text-muted" style={{ fontSize: '0.78rem', marginTop: 4, display: 'block' }}>{locationLabels[currentSlot.location_type] || currentSlot.location_type}</span>}
          </div>
          <div className="form-group">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Klient {loadingClients && <span className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 400 }}>(wird geladen...)</span>}</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={loadingClients}>
              <option value="">— Kein Klient —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
            <p className="text-muted text-sm" style={{ marginBottom: 8 }}>Gast-Informationen (bei Buchung ohne Konto)</p>
            <div className="form-row">
              <div className="form-group"><label>Gast-Name</label><input type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Vor- und Nachname" /></div>
              <div className="form-group"><label>Gast-E-Mail</label><input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="email@beispiel.de" /></div>
            </div>
            <div className="form-group"><label>Nachricht vom Gast</label><textarea value={guestMessage} onChange={(e) => setGuestMessage(e.target.value)} rows={2} placeholder="Nachricht des Gastes..." /></div>
          </div>
          <div className="form-group"><label>Notizen</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Interne Notizen zur Buchung..." /></div>
        </div>
        <div className="modal-footer" style={{ padding: '16px 24px' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Speichern...' : 'Änderungen speichern'}</button>
        </div>
      </div>
    </div>
  );
}


function EventDetailModal({ event, onClose, onZoomCreated }: { event: CalendarEvent; onClose: () => void; onZoomCreated: () => void }) {
  const { showToast } = useToast();
  const [creatingZoom, setCreatingZoom] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [copied, setCopied] = useState(false);

  // Slot → Buchung erstellen (vollständiges Formular)
  const [showBookSlot, setShowBookSlot] = useState(false);
  const [slotClients, setSlotClients] = useState<Client[]>([]);
  const [loadingSlotClients, setLoadingSlotClients] = useState(false);
  const [slotClientId, setSlotClientId] = useState('');
  const [slotNotes, setSlotNotes] = useState('');
  const [slotLocationType, setSlotLocationType] = useState(event.location_type || 'both');
  const [slotGuestName, setSlotGuestName] = useState('');
  const [slotGuestEmail, setSlotGuestEmail] = useState('');
  const [bookingSlot, setBookingSlot] = useState(false);

  const handleBookSlot = () => {
    setShowBookSlot(true);
    setLoadingSlotClients(true);
    clientsApi.list({ limit: 200 }).then((res) => setSlotClients(res.data.items)).finally(() => setLoadingSlotClients(false));
  };

  const handleCreateBookingFromSlot = async () => {
    setBookingSlot(true);
    try {
      // Slot-Ort aktualisieren falls geändert
      if (slotLocationType !== event.location_type) {
        await bookingSlotsApi.update(event.id, { location_type: slotLocationType });
      }
      await bookingsApi.create({
        booking_slot_id: event.id,
        client_id: slotClientId || undefined,
        notes: slotNotes || undefined,
        guest_name: slotGuestName || undefined,
        guest_email: slotGuestEmail || undefined,
      });
      const selectedClient = slotClients.find((c) => c.id === slotClientId);
      const clientName = selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : null;
      const guestLabel = !clientName && slotGuestName ? slotGuestName : null;
      const nameLabel = clientName || guestLabel;
      showToast(nameLabel ? `Termin für ${nameLabel} gebucht` : 'Slot als Termin gebucht', 'success');
      onZoomCreated();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Buchen', 'error');
    } finally { setBookingSlot(false); }
  };

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  const typeLabels: Record<string, string> = { session: 'Sitzung', booking: 'Buchung', slot: 'Verfügbar' };
  const locationLabels: Record<string, string> = { in_person: 'Vor Ort', online: 'Online', both: 'Vor Ort oder Online' };

  const handleCreateZoom = async () => {
    setCreatingZoom(true);
    try {
      await sessionsApi.createZoomMeeting(event.id);
      onZoomCreated();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Zoom-Meeting konnte nicht erstellt werden', 'error');
    } finally { setCreatingZoom(false); }
  };

  const handleCancelSession = async () => {
    if (!window.confirm('Sitzung wirklich stornieren? Der Klient wird per E-Mail benachrichtigt.')) return;
    setCancelling(true);
    try {
      await sessionsApi.cancel(event.id);
      onZoomCreated(); // reloads events + closes modal
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Stornierung fehlgeschlagen', 'error');
    } finally { setCancelling(false); }
  };

  const handleCancelBooking = async () => {
    if (!window.confirm('Buchung wirklich stornieren?')) return;
    setCancelling(true);
    try {
      await bookingsApi.update(event.id, { status: 'cancelled', cancellation_reason: 'Vom Coach storniert' });
      onZoomCreated(); // reloads events + closes modal
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Stornierung fehlgeschlagen', 'error');
    } finally { setCancelling(false); }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: event.type === 'slot' ? '#5B7EA8' : 'var(--primary)' }} />
            {event.title}
            {event.google_event_id && (
              <span title="Mit Google Calendar synchronisiert">
                <Cloud size={14} style={{ color: '#4285F4', marginLeft: 4 }} />
              </span>
            )}
          </h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>
        <div style={{ padding: '0 24px 20px' }}>
          {/* Datum & Zeit */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div className="text-muted" style={{ fontSize: '0.78rem', marginBottom: 2 }}>Datum</div>
              <div style={{ fontWeight: 500 }}>{fmtDate(event.start)}</div>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: '0.78rem', marginBottom: 2 }}>Uhrzeit</div>
              <div style={{ fontWeight: 500 }}>{fmtTime(event.start)} — {fmtTime(event.end)}</div>
            </div>
          </div>

          {/* Typ & Status */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <span className="badge badge-new" style={{ fontSize: '0.78rem' }}>{typeLabels[event.type] || event.type}</span>
            <span className={`badge ${event.status === 'confirmed' || event.status === 'scheduled' ? 'badge-active' : event.status === 'cancelled' ? 'badge-paused' : 'badge-new'}`} style={{ fontSize: '0.78rem' }}>
              {event.status === 'scheduled' ? 'Geplant' : event.status === 'confirmed' ? 'Bestätigt' : event.status === 'completed' ? 'Abgeschlossen' : event.status === 'cancelled' ? 'Storniert' : event.status === 'available' ? 'Verfügbar' : event.status}
            </span>
            {event.location_type && (
              <span className="badge" style={{ fontSize: '0.78rem', background: event.location_type === 'online' ? '#e8f4fd' : '#f0f0f0', color: event.location_type === 'online' ? '#2D8CFF' : '#666' }}>
                {locationLabels[event.location_type] || event.location_type}
              </span>
            )}
          </div>

          {/* Slot → Buchung erstellen */}
          {event.type === 'slot' && !showBookSlot && (
            <button
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: 16 }}
              onClick={handleBookSlot}
            >
              <Plus size={16} /> Termin buchen
            </button>
          )}
          {event.type === 'slot' && showBookSlot && (
            <div style={{ padding: '16px 18px', background: 'var(--bg)', borderRadius: 8, marginBottom: 16, border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 12 }}>Buchung für diesen Slot</div>

              {/* Ort */}
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label style={{ fontSize: '0.82rem' }}>Ort</label>
                <select value={slotLocationType} onChange={(e) => setSlotLocationType(e.target.value)}>
                  <option value="both">Vor Ort & Online</option>
                  <option value="in_person">Nur vor Ort</option>
                  <option value="online">Nur Online</option>
                </select>
              </div>
              {(slotLocationType === 'online' || slotLocationType === 'both') && (
                <div style={{ padding: '8px 12px', background: '#e8f4fd', borderRadius: 6, marginBottom: 10, fontSize: '0.82rem', color: '#2D8CFF', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Video size={14} /> Zoom-Meeting wird beim Erstellen der Sitzung hinzugefügt
                </div>
              )}

              {/* Klient */}
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label style={{ fontSize: '0.82rem' }}>
                  Klient {loadingSlotClients && <span className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 400 }}>(wird geladen...)</span>}
                </label>
                <select value={slotClientId} onChange={(e) => setSlotClientId(e.target.value)} disabled={loadingSlotClients}>
                  <option value="">— Kein Klient (Gast) —</option>
                  {slotClients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
              </div>

              {/* Gast-Felder (nur wenn kein Klient ausgewählt) */}
              {!slotClientId && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label style={{ fontSize: '0.82rem' }}>Gastname</label>
                    <input type="text" value={slotGuestName} onChange={(e) => setSlotGuestName(e.target.value)} placeholder="Name des Gastes" />
                  </div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label style={{ fontSize: '0.82rem' }}>Gast-E-Mail</label>
                    <input type="email" value={slotGuestEmail} onChange={(e) => setSlotGuestEmail(e.target.value)} placeholder="E-Mail-Adresse" />
                  </div>
                </div>
              )}

              {/* Notizen */}
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '0.82rem' }}>Notizen</label>
                <textarea value={slotNotes} onChange={(e) => setSlotNotes(e.target.value)} rows={2} placeholder="Optional..." />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleCreateBookingFromSlot} disabled={bookingSlot || loadingSlotClients}>
                  {bookingSlot ? 'Wird gebucht...' : 'Jetzt buchen'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowBookSlot(false)}>Abbrechen</button>
              </div>
            </div>
          )}

          {/* Zoom-Link vorhanden */}
          {event.online_session?.meeting_link && (
            <div style={{ padding: '12px 16px', background: '#e8f4fd', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Video size={18} style={{ color: '#2D8CFF' }} />
                <span style={{ fontWeight: 600, color: '#2D8CFF' }}>Zoom-Meeting</span>
              </div>
              <div style={{ fontSize: '0.85rem', marginBottom: 8, wordBreak: 'break-all', color: 'var(--text)' }}>
                {event.online_session.meeting_link}
              </div>
              {event.online_session.access_code && (
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 10 }}>
                  Passcode: <strong>{event.online_session.access_code}</strong>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <a
                  href={event.online_session.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm"
                  style={{ background: '#2D8CFF', color: 'white', border: 'none', textDecoration: 'none', padding: '6px 14px' }}
                >
                  <ExternalLink size={14} /> Meeting öffnen
                </a>
                <button
                  className="btn btn-sm btn-secondary"
                  style={{ padding: '6px 14px' }}
                  onClick={() => handleCopy(event.online_session!.meeting_link!)}
                >
                  <Copy size={14} /> {copied ? 'Kopiert!' : 'Link kopieren'}
                </button>
              </div>
            </div>
          )}

          {/* Online-Sitzung ohne Zoom → "Zoom erstellen" Button */}
          {event.type === 'session' && event.location_type === 'online' && !event.online_session?.meeting_link && (
            <div style={{ padding: '12px 16px', background: '#fef9e7', borderRadius: 8, marginBottom: 16, border: '1px solid #f0e68c' }}>
              <div style={{ fontSize: '0.85rem', color: '#856404', marginBottom: 8 }}>
                Diese Online-Sitzung hat noch kein Zoom-Meeting.
              </div>
              <button
                className="btn btn-sm"
                style={{ background: '#2D8CFF', color: 'white', border: 'none', padding: '6px 14px' }}
                onClick={handleCreateZoom}
                disabled={creatingZoom}
              >
                <Video size={14} /> {creatingZoom ? 'Erstelle...' : 'Zoom-Meeting erstellen'}
              </button>
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ padding: '12px 24px', justifyContent: 'space-between' }}>
          {event.type === 'session' && (event.status === 'scheduled' || event.status === 'in_progress') ? (
            <button
              className="btn btn-sm"
              onClick={handleCancelSession}
              disabled={cancelling}
              style={{ background: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Ban size={14} /> {cancelling ? 'Wird storniert...' : 'Sitzung stornieren'}
            </button>
          ) : event.type === 'booking' && (event.status === 'confirmed' || event.status === 'pending') ? (
            <button
              className="btn btn-sm"
              onClick={handleCancelBooking}
              disabled={cancelling}
              style={{ background: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Ban size={14} /> {cancelling ? 'Wird storniert...' : 'Buchung stornieren'}
            </button>
          ) : <span />}
          <button className="btn btn-secondary" onClick={onClose}>Schließen</button>
        </div>
      </div>
    </div>
  );
}


// ==================== OVERLAP WARNING ====================

function OverlapWarningModal({ conflicts, onClose }: {
  conflicts: Array<{ id: string; start_time: string; end_time: string; title: string }>;
  onClose: () => void;
}) {
  const fmtDT = (iso: string) => new Date(iso).toLocaleString('de-DE', {
    weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10000 }}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#d97706' }}>
            <AlertTriangle size={20} /> Terminkollision
          </h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>
        <div style={{ padding: '0 24px 8px' }}>
          <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
            Dieser Zeitraum überschneidet sich mit bestehenden Terminen:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {conflicts.map((c) => (
              <div key={c.id} style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{c.title}</div>
                <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: 2 }}>
                  {fmtDT(c.start_time)} – {fmtTime(c.end_time)}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Zurück zum Formular</button>
        </div>
      </div>
    </div>
  );
}


// ==================== HELPERS ====================

function chunkArray<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
}

function getWeekNumber(d: Date): number {
  const target = new Date(d.valueOf());
  const dayNum = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNum + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
}
